import type { BlockedIP } from '@/mocks/adminModeracion';

interface Props {
  ips: BlockedIP[];
  onToggleActive: (id: string) => void;
}

export default function BlockedIPsList({ ips, onToggleActive }: Props) {
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const activeIPs = ips.filter((ip) => ip.active);

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'IPs bloqueadas', value: activeIPs.length, icon: 'ri-global-line', color: 'text-red-500' },
          { label: 'Bloqueos por sistema', value: ips.filter((ip) => ip.blockedBy === 'Sistema').length, icon: 'ri-robot-2-line', color: 'text-violet-500' },
          { label: 'IPs liberadas', value: ips.filter((ip) => !ip.active).length, icon: 'ri-check-line', color: 'text-emerald-500' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className={`${s.icon} ${s.color} text-sm`}></i>
              </div>
              <span className="text-[10px] text-foreground-400 font-medium">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* IP Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ips.map((ip) => (
          <div key={ip.id} className={`bg-background-50 border rounded-xl p-4 transition-all ${ip.active ? 'border-background-200' : 'border-background-100 opacity-60'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ip.active ? 'bg-red-100 text-red-600' : 'bg-background-200 text-foreground-400'}`}>
                  <i className={`${ip.active ? 'ri-global-line' : 'ri-global-line'} text-sm`}></i>
                </div>
                <code className="text-xs font-bold text-foreground-900 font-mono">{ip.ip}</code>
              </div>
              <button
                onClick={() => onToggleActive(ip.id)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  ip.active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                }`}
              >
                {ip.active ? 'Desbloquear' : 'Re-bloquear'}
              </button>
            </div>
            <p className="text-[10px] text-foreground-600 mb-2 leading-relaxed">{ip.reason}</p>
            {ip.notes && (
              <div className="bg-background-100 rounded-lg px-3 py-2 mb-2">
                <p className="text-[10px] text-foreground-500">
                  <i className="ri-sticky-note-line text-foreground-400 mr-1"></i>
                  {ip.notes}
                </p>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-background-100">
              <span className="text-[10px] text-foreground-400">{formatDate(ip.blockedAt)}</span>
              <span className="text-[10px] text-foreground-500 font-medium">{ip.blockedBy}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}